import React, { useState } from 'react';
import { Linking, Pressable, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { timeAgo } from '../../../lib/date';
import { useMut } from '../../../lib/query';
import { Badge, Button, Card, EmptyState, Loading, PageHeader, Row, Screen, Tabs, Text, useTheme } from '../../../ui';

export function NotificationsScreen() {
  const { colors } = useTheme();
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['notifications', 'list', tab, page], queryFn: () => api.ess.notifications({ page, unread: tab === 'unread' ? 1 : undefined }) });
  const read = useMut((id: number) => api.ess.readNotification(id), { invalidate: [['notifications'], ['dashboard']] });
  const readAll = useMut(() => api.ess.readAll(), { invalidate: [['notifications'], ['dashboard']], success: 'All marked as read' });
  const data = q.data;
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()}>
      <PageHeader title="Notifications" actions={<Button icon="check" title="Mark all read" loading={readAll.isPending} onPress={() => readAll.mutate(undefined)} />} />
      <Tabs tabs={[{ key: 'all', label: 'All' }, { key: 'unread', label: 'Unread' }]} value={tab} onChange={(k) => { setTab(k); setPage(1); }} />
      <Card flush>
        {q.isLoading ? (
          <Loading />
        ) : !data?.data.length ? (
          <EmptyState icon="bell" title="No notifications" />
        ) : (
          data.data.map((n, i) => (
            <Pressable
              key={n.id}
              accessibilityRole="button"
              onPress={() => {
                if (!n.read_at) read.mutate(n.id);
                const link = n.data?.link;
                if (typeof link === 'string' && /^https?:/.test(link)) void Linking.openURL(link);
              }}
              style={({ pressed }) => ({ flexDirection: 'row', gap: 12, paddingHorizontal: 16, paddingVertical: 12, backgroundColor: pressed ? colors.hover : !n.read_at ? colors.primarySoft : 'transparent', borderBottomWidth: i < data.data.length - 1 ? 1 : 0, borderBottomColor: colors.border })}
            >
              <View style={{ width: 8, height: 8, borderRadius: 4, marginTop: 6, backgroundColor: n.read_at ? 'transparent' : colors.primary }} />
              <View style={{ flex: 1, gap: 2 }}>
                <Row justify="space-between" wrap={false}>
                  <Text weight="semibold" style={{ flex: 1 }} numberOfLines={1}>
                    {n.title}
                  </Text>
                  <Text variant="small">{timeAgo(n.created_at)}</Text>
                </Row>
                <Text>{n.body}</Text>
                {n.channel !== 'in_app' && <Badge>{n.channel}</Badge>}
              </View>
            </Pressable>
          ))
        )}
      </Card>
      {data && data.last_page > 1 && (
        <Row justify="flex-end">
          <Button size="sm" title="Prev" disabled={page <= 1} onPress={() => setPage(page - 1)} />
          <Text variant="small">
            Page {data.current_page} / {data.last_page}
          </Text>
          <Button size="sm" title="Next" disabled={page >= data.last_page} onPress={() => setPage(page + 1)} />
        </Row>
      )}
    </Screen>
  );
}
